/**
 * Phase 21.1g — Admin Live Health Dashboard
 * -----------------------------------------
 * One pane to see every live room's:
 *   - LiveKit project health (from /api/live/livekit/health)
 *   - Speakers / listeners / queue / ghost participants
 *   - Force-refresh + reap-ghosts + cleanup-stale-rooms controls
 *
 * Auto-refreshes every 8s. Admin-only — server returns 403 otherwise.
 */
import { useCallback, useEffect, useState, Fragment } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";

const formatMin = (m) => {
  if (!m) return "just now";
  if (m < 60) return `${m}m live`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m live` : `${h}h live`;
};

const StatusPill = ({ status }) => {
  const map = {
    healthy: { bg: "bg-emerald-500", text: "✅ Healthy" },
    rate_limited: { bg: "bg-rose-500", text: "⛔ Rate limited" },
    auth_failed: { bg: "bg-amber-500", text: "🔑 Auth failed" },
    degraded: { bg: "bg-amber-500", text: "⚠️ Degraded" },
    unconfigured: { bg: "bg-gray-500", text: "❓ Unconfigured" },
  };
  const v = map[status] || { bg: "bg-gray-600", text: status || "unknown" };
  return (
    <span
      className={`text-[10px] font-black px-2 py-1 rounded-full text-white ${v.bg}`}
      data-testid={`lk-status-pill-${status}`}
    >
      {v.text}
    </span>
  );
};

const RoomRow = ({ room, onReap, onForceRefresh, onRebuild, onReconnect, busyRoomId }) => {
  const busy = busyRoomId === room.room_id;
  const hasGhosts = (room.ghost_count || 0) > 0;
  return (
    <tr
      className="border-b border-white/5 hover:bg-white/5"
      data-testid={`live-health-row-${room.room_id}`}
    >
      <td className="p-2">
        <Link to={`/beams/${room.room_id}`} className="text-pink-300 hover:underline">
          {room.title}
        </Link>
        <p className="text-[10px] text-gray-400">{room.host_name}</p>
      </td>
      <td className="p-2 text-center text-emerald-300">{room.ws_online_count}</td>
      <td className="p-2 text-center">
        <div className="inline-flex items-center justify-center">
          <span className="text-rose-200">{room.speaker_count}</span>
          {hasGhosts && (
            <span
              className="ml-1 text-[10px] font-bold text-amber-300"
              data-testid={`live-health-ghost-count-${room.room_id}`}
              title={`${room.ghost_count} ghost(s) detected`}
            >
              (+{room.ghost_count} 👻)
            </span>
          )}
        </div>
      </td>
      <td className="p-2 text-center text-sky-200">{room.queue_length}</td>
      <td className="p-2 text-center text-amber-200">{(room.gift_total_coins || 0).toLocaleString()}c</td>
      <td className="p-2 text-[10px] text-gray-400">{formatMin(room.minutes_live)}</td>
      <td className="p-2">
        <div className="flex gap-1 justify-end flex-wrap">
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !hasGhosts}
            onClick={() => onReap(room.room_id)}
            data-testid={`live-health-reap-btn-${room.room_id}`}
            className="text-[10px] px-2 py-0.5"
            title={hasGhosts ? "Remove ghost participants" : "No ghosts to remove"}
          >
            👻 Reap
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onForceRefresh(room.room_id)}
            data-testid={`live-health-refresh-btn-${room.room_id}`}
            className="text-[10px] px-2 py-0.5"
            title="Broadcast force_refresh to every viewer"
          >
            ↻ Refresh
          </Button>
          {/* Phase 21.3 — hard reset participant list */}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onRebuild(room.room_id)}
            data-testid={`live-health-rebuild-btn-${room.room_id}`}
            className="text-[10px] px-2 py-0.5 border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
            title="Hard reset: drop every guest except host, clear queue + mutes, force-refresh clients"
          >
            🛠 Rebuild
          </Button>
          {/* Phase 21.3a — graceful reconnect (no presence mutation) */}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onReconnect(room.room_id)}
            data-testid={`live-health-reconnect-btn-${room.room_id}`}
            className="text-[10px] px-2 py-0.5 border-sky-500/40 text-sky-200 hover:bg-sky-500/10"
            title="Graceful reconnect: ask every client to re-fetch + re-publish, no presence wipe"
          >
            🔁 Reconnect
          </Button>
        </div>
      </td>
    </tr>
  );
};

const AdminLiveHealthPage = () => {
  const [lkHealth, setLkHealth] = useState(null);
  const [healthData, setHealthData] = useState(null);
  // iter_178 — On-screen diagnostics dataset (used by both the table AND
  // the CSV export so what you see is what you download).
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState(null);
  const [busyRoomId, setBusyRoomId] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [loading, setLoading] = useState(true);
  // 21.1h — Refresh state: separate from initial `loading` so the UI
  // can show a spinner on click + a last-refreshed timestamp + error.
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  // 21.3 — global admin recovery actions
  const [forceReconciling, setForceReconciling] = useState(false);
  const [lastReconcileSummary, setLastReconcileSummary] = useState(null);
  // 21.3 — passive WS status probe (Connected / Reconnecting / Disconnected)
  // Mirrors what every live-room client sees, surfaced one level up so
  // the admin knows the global signalling channel is healthy.
  const [wsStatus, setWsStatus] = useState("connecting"); // 'connected' | 'reconnecting' | 'disconnected'

  // iter_181 (P0) — Expandable-row state so admins can drill into a
  // room's client-side event stream + disconnect-reason breakdown.
  const [expandedRoom, setExpandedRoom] = useState(null);
  const toggleExpanded = (rid) => {
    setExpandedRoom((cur) => (cur === rid ? null : rid));
  };

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const [a, b] = await Promise.all([
        api.get("/live/livekit/health"),
        api.get("/admin/live/health"),
      ]);
      setLkHealth(a.data);
      setHealthData(b.data);
      setLastRefreshedAt(new Date());
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || "Refresh failed — couldn't reach backend";
      setRefreshError(msg);
      if (e?.response?.status === 403) {
        toast.error("Admin only");
      } else if (e?.response?.status === 401) {
        toast.error("Sign in as admin to view this dashboard");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  // 21.1h — tiny "Xs ago" / "just now" ticker so the timestamp feels live.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClockTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const lastRefreshedLabel = (() => {
    if (!lastRefreshedAt) return "never";
    const s = Math.max(0, Math.floor((Date.now() - lastRefreshedAt.getTime()) / 1000));
    if (s < 2) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s ago`;
  })();

  const onReap = async (rid) => {
    setBusyRoomId(rid);
    try {
      const r = await api.post(`/live/rooms/${rid}/admin/reap-ghosts`);
      toast.success(`Reaped ${r.data.reaped} ghost(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reap ghosts");
    } finally {
      setBusyRoomId(null);
    }
  };

  const onForceRefresh = async (rid) => {
    setBusyRoomId(rid);
    try {
      await api.post(`/live/rooms/${rid}/admin/force-refresh`);
      toast.success("Force-refresh broadcast sent");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't force-refresh");
    } finally {
      setBusyRoomId(null);
    }
  };

  const onCleanupStale = async () => {
    setCleaningUp(true);
    try {
      const r = await api.post("/live/livekit/cleanup-stale-rooms");
      toast.success(`Deleted ${r.data.deleted_count} stale LiveKit room(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningUp(false);
    }
  };

  // Phase 21.3 — Force Reconcile All
  const onForceReconcileAll = async () => {
    if (!window.confirm(
      "Force-reconcile EVERY live room? This reaps ghost participants " +
      "and broadcasts a force-refresh to every connected client. Continue?"
    )) return;
    setForceReconciling(true);
    try {
      const r = await api.post("/admin/live/force-reconcile-all");
      setLastReconcileSummary({
        ...r.data,
        at: new Date(),
      });
      toast.success(
        `Reconciled ${r.data.rooms_scanned} room(s) · ${r.data.ghosts_reaped} ghost(s) reaped`
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force reconcile failed");
    } finally {
      setForceReconciling(false);
    }
  };

  // Phase 21.3 — Rebuild Participant State per room
  const onRebuildParticipants = async (rid) => {
    if (!window.confirm(
      `Rebuild participant state for ${rid}?\n\n` +
      "This drops EVERY guest except the host, clears the queue and " +
      "mutes, and forces every client to re-fetch. Use only when reap " +
      "isn't enough. Continue?"
    )) return;
    setBusyRoomId(rid);
    try {
      const r = await api.post(`/live/rooms/${rid}/admin/rebuild-participants`);
      toast.success(
        `Rebuilt ${rid} — ${r.data.dropped_count} guest(s) dropped`
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setBusyRoomId(null);
    }
  };

  // Phase 21.3a — Reconnect Room (graceful: broadcast force_refresh
  // without mutating presence). Lighter than Rebuild.
  const onReconnectRoom = async (rid) => {
    setBusyRoomId(rid);
    try {
      await api.post(`/admin/live/reconnect-room/${rid}`);
      toast.success(`Reconnect signal sent to ${rid}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconnect failed");
    } finally {
      setBusyRoomId(null);
    }
  };

  // Phase 21.3a → iter_178 — Export diagnostics CSV. Downloads the same
  // records the on-screen table shows (server-side query is identical).
  const onExportDiagnostics = async () => {
    try {
      const r = await api.get("/admin/live/diagnostics-export", {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rgm-live-diagnostics-${new Date()
        .toISOString().replace(/[:.]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Diagnostics CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  // iter_178 — On-screen diagnostics: currently active + last 24h ended rooms.
  const loadDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    setDiagError(null);
    try {
      const r = await api.get("/admin/live/diagnostics");
      setDiag(r.data || null);
    } catch (e) {
      setDiag(null);
      setDiagError(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => { loadDiagnostics(); }, [loadDiagnostics]);

  // Phase 21.3 — Passive WS health probe. Opens a lightweight WS to the
  // backend (the live ws endpoint requires a roomId, so we ping the
  // generic notifications WS instead, which every authenticated client
  // already uses). The probe auto-reconnects on close.
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let cancelled = false;
    const connect = () => {
      try {
        const token = localStorage.getItem("session_token") || "";
        const httpBase = api.defaults.baseURL || window.location.origin;
        const proto = httpBase.startsWith("https://") ? "wss://" : "ws://";
        const host = httpBase.replace(/^https?:\/\//, "").replace(/\/api$/, "");
        const url = `${proto}${host}/api/notifications/ws?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(url);
        ws.onopen = () => { if (!cancelled) setWsStatus("connected"); };
        ws.onclose = () => {
          if (cancelled) return;
          setWsStatus("disconnected");
          reconnectTimer = setTimeout(() => {
            if (cancelled) return;
            setWsStatus("reconnecting");
            connect();
          }, 4000);
        };
        ws.onerror = () => { if (!cancelled) setWsStatus("disconnected"); };
      } catch (_e) {
        if (!cancelled) setWsStatus("disconnected");
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch (_e) { /* ignore */ }
    };
  }, []);

  const wsStatusStyle = (() => {
    if (wsStatus === "connected") return { dot: "bg-emerald-500", label: "Connected", text: "text-emerald-300" };
    if (wsStatus === "reconnecting") return { dot: "bg-amber-400", label: "Reconnecting", text: "text-amber-200" };
    if (wsStatus === "connecting") return { dot: "bg-gray-400", label: "Connecting…", text: "text-gray-300" };
    return { dot: "bg-rose-500", label: "Disconnected", text: "text-rose-300" };
  })();

  // Phase 21.3a5 — Back button + platform navigation shell so admins can
  // return to the previous page without using the browser back gesture.
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen bg-[#0c0814] text-white"
      data-testid="admin-live-health-page"
    >
      <SharedNavigation />
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/5"
            data-testid="admin-live-health-back-btn"
          >
            ← Back
          </Button>
          <Link
            to="/admin/control-center"
            className="text-[11px] text-purple-300 hover:text-purple-200"
            data-testid="admin-live-health-to-control-center"
          >
            Control Center →
          </Link>
        </div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">
              📡 Admin Live Health
            </h1>
            <p className="text-sm text-gray-400">
              LiveKit project status · active rooms · ghost cleanup · force-refresh tools
            </p>
            <p
              className="text-[11px] text-gray-500 mt-1"
              data-testid="admin-live-health-last-refreshed"
            >
              {refreshing
                ? "Refreshing…"
                : `Last refreshed ${lastRefreshedLabel}`}
              {refreshError && (
                <span
                  className="ml-2 text-rose-300"
                  data-testid="admin-live-health-refresh-error"
                >
                  · ⚠ {refreshError}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Phase 21.3 — WebSocket status indicator */}
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold ${wsStatusStyle.text}`}
              data-testid="admin-live-health-ws-status"
              title="Notifications WebSocket status"
            >
              <span
                className={`w-2 h-2 rounded-full ${wsStatusStyle.dot}`}
                data-testid={`admin-live-health-ws-dot-${wsStatus}`}
              />
              WS · {wsStatusStyle.label}
            </span>
            {/* Phase 21.3 — Force Reconcile All */}
            <Button
              onClick={onForceReconcileAll}
              disabled={forceReconciling}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="admin-live-health-force-reconcile-all-btn"
              title="Reap ghosts in every live room + broadcast force-refresh"
            >
              {forceReconciling ? "🛠 Reconciling…" : "🛠 Force Reconcile All"}
            </Button>
            {/* Phase 21.3a → iter_178 — Export Diagnostics CSV */}
            <Button
              onClick={onExportDiagnostics}
              variant="outline"
              data-testid="admin-live-health-export-diagnostics-btn"
              title="Download a CSV of the currently visible diagnostics rows"
              className="border-sky-500/40 text-sky-200 hover:bg-sky-500/10"
            >
              📊 Export Diagnostics
            </Button>
            {/* iter_178 — Refresh Diagnostics — reloads the on-screen table */}
            <Button
              onClick={loadDiagnostics}
              variant="outline"
              disabled={diagLoading}
              data-testid="admin-live-health-refresh-diagnostics-btn"
              title="Reload the diagnostics table (live + last 24h ended rooms)"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
            >
              {diagLoading ? "🔄 Refreshing…" : "🔄 Refresh Diagnostics"}
            </Button>
            <Button
              onClick={refresh}
              variant="outline"
              disabled={refreshing}
              data-testid="admin-live-health-refresh-btn"
            >
              {refreshing ? "↻ Refreshing…" : "↻ Refresh now"}
            </Button>
          </div>
        </div>

        {/* Phase 21.3 — Last reconcile summary */}
        {lastReconcileSummary && (
          <Card
            className="p-3 mb-3 bg-rose-500/5 border-rose-500/20 text-xs"
            data-testid="admin-live-health-last-reconcile"
          >
            <span className="text-rose-200 font-bold">Last reconcile · </span>
            <span className="text-gray-200">
              {lastReconcileSummary.at?.toLocaleTimeString?.()}: scanned{" "}
              <span className="text-emerald-300">{lastReconcileSummary.rooms_scanned}</span>{" "}
              room(s), touched{" "}
              <span className="text-amber-200">{lastReconcileSummary.rooms_touched}</span>,
              reaped{" "}
              <span className="text-rose-200">{lastReconcileSummary.ghosts_reaped}</span>{" "}
              ghost(s), force-refreshed{" "}
              <span className="text-sky-200">{lastReconcileSummary.rooms_force_refreshed}</span>{" "}
              client pool(s).
            </span>
          </Card>
        )}

        {/* iter_178 — Diagnostics table (live + last 24h ended rooms).
            Same records exported by the CSV button so what you see is
            what you download. */}
        <Card className="p-4 mb-4 bg-white/[0.03] border-white/10" data-testid="admin-live-diagnostics-table">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <h2 className="text-sm font-black text-white">📋 Diagnostics — active + last 24h</h2>
              {diag && (
                <p className="text-[10px] text-gray-500 font-mono" data-testid="admin-live-diagnostics-meta">
                  collection={diag.collection} · filter={diag.filter_summary} · count={diag.count}
                </p>
              )}
            </div>
          </div>
          {diagLoading && !diag && (
            <p className="text-xs text-gray-500" data-testid="admin-live-diagnostics-loading">Loading diagnostics…</p>
          )}
          {diagError && (
            <p className="text-xs text-rose-300" data-testid="admin-live-diagnostics-error">
              Error: {diagError}
            </p>
          )}
          {diag && diag.count === 0 && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100" data-testid="admin-live-diagnostics-empty">
              {diag.diagnostic_message}
            </div>
          )}
          {diag && diag.count > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="admin-live-diagnostics-rows">
                <thead className="text-left text-gray-400 uppercase tracking-wider text-[10px] border-b border-white/10">
                  <tr>
                    <th className="py-1.5 pr-2 w-6"></th>
                    <th className="py-1.5 pr-2">Room</th>
                    <th className="py-1.5 pr-2">Host</th>
                    <th className="py-1.5 pr-2">Visibility</th>
                    <th className="py-1.5 pr-2">Status</th>
                    <th className="py-1.5 pr-2">Started</th>
                    <th className="py-1.5 pr-2">Ended</th>
                    <th className="py-1.5 pr-2">Duration</th>
                    <th className="py-1.5 pr-2">Participants</th>
                    <th className="py-1.5 pr-2">Peak</th>
                    <th className="py-1.5 pr-2" title="WebSocket disconnects / reconnects (backend)">WS D/R</th>
                    <th className="py-1.5 pr-2" title="Client-side LiveKit disconnects (network drops only)">Client D</th>
                    <th className="py-1.5 pr-2">Failure</th>
                  </tr>
                </thead>
                <tbody>
                  {diag.rooms.map((r) => {
                    const dur = r.duration_seconds
                      ? `${Math.floor(r.duration_seconds / 60)}m ${r.duration_seconds % 60}s`
                      : "—";
                    const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
                    const isOpen = expandedRoom === r.room_id;
                    const clientD = r.client_disconnect_count || 0;
                    const clientR = r.client_reconnected_count || 0;
                    const clientInt = r.client_intentional_leaves || 0;
                    const clientTrans = r.client_transient_disconnects || 0;
                    const reasons = r.client_disconnect_reasons || {};
                    const events = r.client_events || [];
                    return (
                      <Fragment key={r.room_id}>
                        <tr
                          className="border-b border-white/5 align-top cursor-pointer hover:bg-white/5"
                          data-testid={`admin-live-diagnostics-row-${r.room_id}`}
                          onClick={() => toggleExpanded(r.room_id)}
                        >
                          <td className="py-1.5 pr-2 text-center text-gray-400">
                            <span data-testid={`admin-live-diagnostics-expand-${r.room_id}`}>
                              {isOpen ? "▾" : "▸"}
                            </span>
                          </td>
                          <td className="py-1.5 pr-2 font-mono text-white">
                            <div>{r.room_id}</div>
                            <div className="text-gray-500 text-[10px]">{r.title || "(no title)"}</div>
                          </td>
                          <td className="py-1.5 pr-2">
                            <div className="text-white">{r.host_name || "?"}</div>
                            <div className="text-gray-500 font-mono text-[10px]">{r.host_user_id}</div>
                          </td>
                          <td className="py-1.5 pr-2">
                            {r.is_test_mode ? (
                              <span className="px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-100 text-[10px] font-black" data-testid={`admin-live-diagnostics-testmode-${r.room_id}`}>
                                🧪 private_test
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-[10px] font-black">
                                🌐 public
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2">
                            <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-black ${
                              r.status === "live"
                                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-100"
                                : "bg-white/10 border-white/20 text-gray-300"
                            }`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="py-1.5 pr-2 text-gray-300">{fmt(r.started_at)}</td>
                          <td className="py-1.5 pr-2 text-gray-300">{fmt(r.ended_at)}</td>
                          <td className="py-1.5 pr-2 text-white">{dur}</td>
                          <td className="py-1.5 pr-2 text-white">{r.participant_count}</td>
                          <td className="py-1.5 pr-2 text-white">{r.peak_viewers === "" ? "—" : r.peak_viewers}</td>
                          <td className="py-1.5 pr-2 text-gray-300">
                            {(r.disconnect_count === "" ? "—" : r.disconnect_count)}
                            {" / "}
                            {(r.reconnect_count === "" ? "—" : r.reconnect_count)}
                          </td>
                          <td className="py-1.5 pr-2 text-amber-200" data-testid={`admin-live-client-drops-${r.room_id}`}>
                            {clientD}
                            {clientTrans ? <span className="text-gray-400"> (+{clientTrans}t)</span> : null}
                          </td>
                          <td className="py-1.5 pr-2 text-rose-300 max-w-[180px] truncate" title={r.failure_reason}>
                            {r.failure_reason || "—"}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr
                            className="border-b border-white/10 bg-black/40"
                            data-testid={`admin-live-diagnostics-details-${r.room_id}`}
                          >
                            <td></td>
                            <td colSpan={12} className="py-3 pr-2">
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 font-bold">
                                    🔴 Client disconnect reasons
                                  </div>
                                  {Object.keys(reasons).length === 0 ? (
                                    <div className="text-[11px] text-gray-500" data-testid={`admin-live-reasons-empty-${r.room_id}`}>
                                      No client-side disconnects reported.
                                    </div>
                                  ) : (
                                    <ul className="space-y-1" data-testid={`admin-live-reasons-list-${r.room_id}`}>
                                      {Object.entries(reasons)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([reason, count]) => (
                                          <li
                                            key={reason}
                                            className="flex justify-between items-center bg-white/5 rounded px-2 py-1 text-[11px]"
                                            data-testid={`admin-live-reason-${r.room_id}-${reason}`}
                                          >
                                            <span className="text-amber-100 font-mono">{reason}</span>
                                            <span className="text-white font-bold">{count}</span>
                                          </li>
                                        ))}
                                    </ul>
                                  )}
                                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                                    <div className="bg-white/5 rounded px-2 py-1">
                                      <div className="text-gray-400 text-[10px]">Reconnected</div>
                                      <div className="text-emerald-200 font-bold">{clientR}</div>
                                    </div>
                                    <div className="bg-white/5 rounded px-2 py-1">
                                      <div className="text-gray-400 text-[10px]">Intentional leaves</div>
                                      <div className="text-sky-200 font-bold">{clientInt}</div>
                                    </div>
                                    <div className="bg-white/5 rounded px-2 py-1">
                                      <div className="text-gray-400 text-[10px]">Transient (deduped)</div>
                                      <div className="text-gray-300 font-bold">{clientTrans}</div>
                                    </div>
                                    <div className="bg-white/5 rounded px-2 py-1">
                                      <div className="text-gray-400 text-[10px]">Hard drops</div>
                                      <div className="text-rose-200 font-bold">{clientD}</div>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5 font-bold">
                                    📜 Recent client events ({events.length})
                                  </div>
                                  {events.length === 0 ? (
                                    <div className="text-[11px] text-gray-500" data-testid={`admin-live-events-empty-${r.room_id}`}>
                                      No client events captured yet.
                                    </div>
                                  ) : (
                                    <div
                                      className="max-h-64 overflow-y-auto space-y-1 pr-1"
                                      data-testid={`admin-live-events-list-${r.room_id}`}
                                    >
                                      {events.slice().reverse().map((e, i) => {
                                        const isDrop = e.event === "disconnected" && !e.intentional && !e.deduped;
                                        return (
                                          <div
                                            key={i}
                                            className={`flex items-center gap-2 bg-white/5 rounded px-2 py-1 text-[11px] ${isDrop ? "border border-rose-500/30" : ""}`}
                                          >
                                            <span className="text-gray-400 font-mono text-[10px] shrink-0">
                                              {e.ts ? new Date(e.ts).toLocaleTimeString() : "—"}
                                            </span>
                                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-black ${
                                              e.event === "disconnected"
                                                ? (e.intentional ? "bg-sky-500/30 text-sky-100" : (e.deduped ? "bg-gray-500/30 text-gray-100" : "bg-rose-500/30 text-rose-100"))
                                                : e.event === "reconnected"
                                                ? "bg-emerald-500/30 text-emerald-100"
                                                : e.event === "reconnecting"
                                                ? "bg-amber-500/30 text-amber-100"
                                                : "bg-white/10 text-gray-200"
                                            }`}>
                                              {e.event}
                                            </span>
                                            <span className="text-amber-100 font-mono">{e.reason}</span>
                                            <span className="text-gray-400 text-[10px]">· {e.role}</span>
                                            {e.source && e.source !== "unknown" && (
                                              <span
                                                className="text-[10px] text-purple-300 font-mono"
                                                data-testid={`admin-live-event-source-${r.room_id}-${i}`}
                                                title={`source callback: ${e.source}`}
                                              >
                                                ← {e.source}
                                              </span>
                                            )}
                                            {e.session_id && (
                                              <span className="text-[10px] text-gray-500 font-mono" title={`session ${e.session_id}`}>
                                                sess:{String(e.session_id).slice(-6)}
                                              </span>
                                            )}
                                            {e.intentional && (
                                              <span className="text-[10px] text-sky-300 font-bold">intentional</span>
                                            )}
                                            {e.deduped && (
                                              <span className="text-[10px] text-gray-400">transient</span>
                                            )}
                                            {e.duplicate && (
                                              <span className="text-[10px] text-rose-400 font-bold">DUP</span>
                                            )}
                                            {e.duplicate_after_intentional && (
                                              <span className="text-[10px] text-amber-400 font-bold">POST-INT</span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* LiveKit health card */}
        <Card className="p-4 mb-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-pink-500/30">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold">LiveKit Cloud</h2>
            {lkHealth?.status && <StatusPill status={lkHealth.status} />}
          </div>
          {lkHealth ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-gray-400 text-[10px] uppercase">Project</p>
                <p className="font-mono text-pink-200 truncate" data-testid="lk-health-ws-url">
                  {lkHealth.ws_url || "—"}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-[10px] uppercase">API key fingerprint</p>
                <p className="font-mono text-amber-200" data-testid="lk-health-api-key-fp">
                  {lkHealth.api_key_fingerprint || "—"}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-[10px] uppercase">Active LK rooms</p>
                <p className="text-emerald-200 font-bold">
                  {lkHealth.admin_probe?.active_rooms ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-[10px] uppercase">HTTPS probe</p>
                <p className={lkHealth.https_probe?.ok ? "text-emerald-200" : "text-rose-200"}>
                  {lkHealth.https_probe?.status_code ?? "n/a"}{" "}
                  {lkHealth.https_probe?.ok ? "OK" : "FAIL"}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-[10px] uppercase">Token signing</p>
                <p className={lkHealth.token_probe?.ok ? "text-emerald-200" : "text-rose-200"}>
                  {lkHealth.token_probe?.ok ? "OK" : "FAIL"}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-[10px] uppercase">Token TTL</p>
                <p className="text-pink-200">{lkHealth.token_ttl_minutes}m</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{loading ? "Loading…" : "No data"}</p>
          )}
          {lkHealth?.recommendation && (
            <p
              className="mt-3 text-xs text-amber-100 bg-amber-500/10 border border-amber-500/30 rounded-md p-2"
              data-testid="lk-health-recommendation"
            >
              💡 {lkHealth.recommendation}
            </p>
          )}
          <Button
            onClick={onCleanupStale}
            disabled={cleaningUp}
            className="mt-3 bg-rose-500 hover:bg-rose-400"
            data-testid="admin-live-health-cleanup-stale-btn"
          >
            {cleaningUp ? "Cleaning…" : "🧹 Delete stale LiveKit rooms"}
          </Button>
        </Card>

        {/* Active rooms table */}
        <Card className="p-3 bg-white/5 border-white/10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold">
              Live Rooms{" "}
              <span className="text-pink-300 font-normal text-sm">
                ({healthData?.active_room_count ?? 0} active · {healthData?.total_ws_connections ?? 0} viewers)
              </span>
            </h2>
          </div>
          {!healthData ? (
            <p className="text-gray-500 text-sm">{loading ? "Loading…" : "No data"}</p>
          ) : healthData.rooms.length === 0 ? (
            <p className="text-gray-500 text-sm py-6 text-center">
              No active rooms.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-live-health-rooms-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-white/10">
                    <th className="text-left p-2">Room / Host</th>
                    <th className="text-center p-2">Listeners</th>
                    <th className="text-center p-2">Speakers</th>
                    <th className="text-center p-2">Queue</th>
                    <th className="text-center p-2">Gifts</th>
                    <th className="text-left p-2">Duration</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {healthData.rooms.map((rm) => (
                    <RoomRow
                      key={rm.room_id}
                      room={rm}
                      onReap={onReap}
                      onForceRefresh={onForceRefresh}
                      onRebuild={onRebuildParticipants}
                      onReconnect={onReconnectRoom}
                      busyRoomId={busyRoomId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminLiveHealthPage;
